/**
 * Create-batch control for the Batches page.
 *
 * A role-gated "Create batch" button (operations / reviewer / admin — auditor is
 * read-only, mirrored from the server gate) that opens a modal to pick a month
 * (no audit reason — opening a batch is a routine step). On confirm it POSTs
 * /batches; the backend opens the batch for
 * that month, freezes the per-batch config, and autopopulates the month's figures
 * from the DUT feed. On success we invalidate the batch list and navigate to the
 * new batch's detail page (same pattern as Restate in LifecycleActions.tsx).
 *
 * Month selection is a zero-dependency Year + Month Select pair composing the
 * `YYYY-MM` period the backend expects. The "per-batch values" section pre-fills
 * the four per-batch config values (Token/RIN MCV, conversion factors, calc
 * methods) from the live config; changing them before creating uses the new
 * values for THAT batch's calculation. If a batch already exists for the month,
 * the backend returns 400 VALIDATION_ERROR with `details.existing_batch_id`; we
 * surface that inline with a link to it.
 */

import { isApiError } from "@compliance/domain";
import {
  Alert,
  Anchor,
  Button,
  Collapse,
  Divider,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconAdjustments,
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconCircleCheckFilled,
  IconPlus,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useActiveConfig, useCreateBatch } from "../api/hooks";
import type { BatchConfigInput, CalcMethods, ConversionFactors } from "../api/types";
import { useHasRole } from "../auth";
import { todayPacific } from "../lib/dateRange";
import { queryKeys } from "../lib/queryKeys";

const MONTHS: { value: string; label: string }[] = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

/** Previous PT month, as { year, month } strings — the usual "create the just-closed period". */
function previousPacificMonth(): { year: string; month: string } {
  const today = todayPacific(); // "YYYY-MM-DD" in PT
  const curYear = Number(today.slice(0, 4));
  const curMonth = Number(today.slice(5, 7)); // 1..12
  const month = curMonth === 1 ? 12 : curMonth - 1;
  const year = curMonth === 1 ? curYear - 1 : curYear;
  return { year: String(year), month: String(month).padStart(2, "0") };
}

/** Year options: this PT year back through five prior years (covers backfills). */
function yearOptions(): string[] {
  const curYear = Number(todayPacific().slice(0, 4));
  return Array.from({ length: 6 }, (_, i) => String(curYear - i));
}

/** The editable per-batch values, mirrored from the live config defaults. */
type Overrides = {
  token_mcv_mmbtu: string;
  rin_mcv_mmbtu: string;
  conversion_factors: ConversionFactors;
  calc_methods: CalcMethods;
};

export function CreateBatchButton() {
  const canCreate = useHasRole("operations", "reviewer", "admin");
  const [opened, setOpened] = useState(false);

  // Auditor (and any unprivileged role) doesn't see the affordance at all.
  if (!canCreate) return null;

  return (
    <>
      <Button
        leftSection={<IconPlus size={16} />}
        onClick={() => setOpened(true)}
        data-tour="create-batch"
      >
        Create batch
      </Button>
      <CreateBatchModal opened={opened} onClose={() => setOpened(false)} />
    </>
  );
}

/** Resolve the live config's per-batch defaults (for pre-filling the overrides). */
function useConfigDefaults(): Overrides | undefined {
  // Org-scoped: the live config resolves from the session, so pre-fill works
  // even for the very first batch (before any config_version exists to chase).
  const activeQ = useActiveConfig();
  const c = activeQ.data?.content;
  if (!c) return undefined;
  // Pre-fill at the config's FULL precision. These are not display values: the
  // form submits them, and the batch freezes whatever it receives. Rounding
  // them to 2 dp once froze lhv_hhv_ratio 0.9 (for 0.900414190526) and
  // mmbtu_per_gj 0.95 (for 0.947817) into every batch created here, and every
  // Btu, gallon and RIN figure downstream was computed at the rounded value.
  return {
    token_mcv_mmbtu: c.token_mcv_mmbtu,
    rin_mcv_mmbtu: c.rin_mcv_mmbtu,
    conversion_factors: { ...c.conversion_factors },
    calc_methods: { ...c.calc_methods },
  };
}

export function CreateBatchModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const defaults = previousPacificMonth();
  const [year, setYear] = useState(defaults.year);
  const [month, setMonth] = useState(defaults.month);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Per-batch overrides, seeded once from the live config defaults when they load.
  const [overrides, setOverrides] = useState<Overrides | null>(null);

  const configDefaults = useConfigDefaults();
  const create = useCreateBatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Seed the override fields from the config defaults once, while the modal is
  // open. Reset to null on close so reopening re-seeds from the latest config.
  useEffect(() => {
    if (opened && !overrides && configDefaults) setOverrides(configDefaults);
  }, [opened, overrides, configDefaults]);

  const canSubmit = !!year && !!month && !create.isPending;
  const monthLabel = MONTHS.find((m) => m.value === month)?.label ?? month;
  const periodLabel = `${monthLabel} ${year}`;

  const close = () => {
    if (create.isPending) return;
    setAdvancedOpen(false);
    setOverrides(null);
    create.reset();
    onClose();
  };

  const setCf = (key: keyof ConversionFactors, value: string) =>
    setOverrides((o) =>
      o ? { ...o, conversion_factors: { ...o.conversion_factors, [key]: value } } : o,
    );
  const setMethod = (key: keyof CalcMethods, value: string) =>
    setOverrides((o) =>
      o ? { ...o, calc_methods: { ...o.calc_methods, [key]: value } as CalcMethods } : o,
    );

  const onConfirm = async () => {
    // Send the (possibly edited) per-batch values; any field the backend doesn't
    // receive defaults from config. When the config hasn't resolved, omit them
    // entirely and let the backend apply its defaults.
    const batch_config: BatchConfigInput | undefined = overrides
      ? {
          token_mcv_mmbtu: overrides.token_mcv_mmbtu,
          rin_mcv_mmbtu: overrides.rin_mcv_mmbtu,
          conversion_factors: overrides.conversion_factors,
          calc_methods: overrides.calc_methods,
        }
      : undefined;
    try {
      const created = await create.mutateAsync({
        period: `${year}-${month}`,
        batch_config,
      });
      notifications.show({
        title: "Batch created",
        message: `${created.batch_id} · ${periodLabel}`,
        color: "teal",
        icon: <IconCircleCheckFilled size={18} />,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.batches.list() });
      close();
      navigate(`/batches/${created.batch_id}`);
    } catch {
      // Surfaced inline via `create.error`; don't rethrow — this runs in a click
      // handler, so a rethrow would escape as an unhandled rejection.
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="Create batch" centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Open a new RFS reporting batch for a month. The platform pulls the month's figures from
          the database automatically — you can review and refine them on the batch.
        </Text>
        {create.error ? <CreateError error={create.error} /> : null}
        <Group grow align="flex-start" data-tour="create-batch-period">
          <Select
            label="Month"
            data={MONTHS}
            value={month}
            onChange={(v) => setMonth(v ?? month)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            data-autofocus
          />
          <Select
            label="Year"
            data={yearOptions()}
            value={year}
            onChange={(v) => setYear(v ?? year)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
        </Group>
        <Text size="xs" c="dimmed">
          Creates the {periodLabel} reporting period.
        </Text>

        <Divider />
        <Anchor
          component="button"
          type="button"
          size="sm"
          c="dimmed"
          onClick={() => setAdvancedOpen((v) => !v)}
          data-tour="create-batch-advanced"
        >
          <Group gap={4} wrap="nowrap">
            {advancedOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
            <IconAdjustments size={14} />
            <Text span size="sm">
              Per-batch values (advanced)
            </Text>
          </Group>
        </Anchor>
        <Collapse in={advancedOpen}>
          <Stack gap="sm">
            <Text size="xs" c="dimmed">
              Pre-filled from the live configuration. Changes here apply only to this batch's
              calculation; they don't alter the saved configuration.
            </Text>
            {overrides ? (
              <>
                <Group grow>
                  <TextInput
                    label="Token MCV (MMBtu)"
                    value={overrides.token_mcv_mmbtu}
                    onChange={(e) =>
                      setOverrides((o) =>
                        o ? { ...o, token_mcv_mmbtu: e.currentTarget.value } : o,
                      )
                    }
                    styles={{ input: { fontFamily: "monospace" } }}
                  />
                  <TextInput
                    label="RIN MCV (MMBtu)"
                    value={overrides.rin_mcv_mmbtu}
                    onChange={(e) =>
                      setOverrides((o) => (o ? { ...o, rin_mcv_mmbtu: e.currentTarget.value } : o))
                    }
                    styles={{ input: { fontFamily: "monospace" } }}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="LHV/HHV ratio"
                    value={overrides.conversion_factors.lhv_hhv_ratio}
                    onChange={(e) => setCf("lhv_hhv_ratio", e.currentTarget.value)}
                    styles={{ input: { fontFamily: "monospace" } }}
                  />
                  <TextInput
                    label="BTU per scf (HHV)"
                    value={overrides.conversion_factors.btu_per_scf_hhv}
                    onChange={(e) => setCf("btu_per_scf_hhv", e.currentTarget.value)}
                    styles={{ input: { fontFamily: "monospace" } }}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="BTU LHV per RIN"
                    value={overrides.conversion_factors.btu_lhv_per_rin}
                    onChange={(e) => setCf("btu_lhv_per_rin", e.currentTarget.value)}
                    styles={{ input: { fontFamily: "monospace" } }}
                  />
                  <TextInput
                    label="MMBtu per GJ"
                    value={overrides.conversion_factors.mmbtu_per_gj}
                    onChange={(e) => setCf("mmbtu_per_gj", e.currentTarget.value)}
                    styles={{ input: { fontFamily: "monospace" } }}
                  />
                </Group>
                <Group grow>
                  <Select
                    label="Token MMBtu method"
                    data={[
                      { value: "energy_totalizer", label: "energy_totalizer" },
                      { value: "scf_ch4_btu", label: "scf_ch4_btu" },
                    ]}
                    value={overrides.calc_methods.token_mmbtu}
                    onChange={(v) => v && setMethod("token_mmbtu", v)}
                    allowDeselect={false}
                    comboboxProps={{ withinPortal: true }}
                  />
                  <Select
                    label="Source granularity"
                    data={[
                      { value: "daily", label: "daily" },
                      { value: "hourly_summed", label: "hourly_summed" },
                    ]}
                    value={overrides.calc_methods.source_granularity}
                    onChange={(v) => v && setMethod("source_granularity", v)}
                    allowDeselect={false}
                    comboboxProps={{ withinPortal: true }}
                  />
                </Group>
              </>
            ) : (
              <Text size="xs" c="dimmed">
                Defaults unavailable — the batch will use the backend configuration defaults.
              </Text>
            )}
          </Stack>
        </Collapse>

        <Group justify="flex-end">
          <Button variant="default" onClick={close} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            disabled={!canSubmit}
            loading={create.isPending}
            onClick={onConfirm}
            data-tour="create-batch-confirm"
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function CreateError({ error }: { error: unknown }) {
  const title = isApiError(error)
    ? `Couldn't create batch (${error.code})`
    : "Couldn't create batch";
  const message = isApiError(error) ? error.message : ((error as Error)?.message ?? String(error));
  // A duplicate-month rejection carries the existing batch id — link to it.
  const existingId =
    isApiError(error) && typeof error.details?.existing_batch_id === "string"
      ? error.details.existing_batch_id
      : null;

  return (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />} title={title}>
      <Text size="sm">{message}</Text>
      {existingId ? (
        <Text size="sm" mt={4}>
          <Anchor component={Link} to={`/batches/${existingId}`}>
            Open the existing batch ({existingId})
          </Anchor>
        </Text>
      ) : null}
      {isApiError(error) ? (
        <Text size="xs" c="dimmed" mt={4} ff="monospace">
          request_id {error.requestId}
        </Text>
      ) : null}
    </Alert>
  );
}
